import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  AutoLoanWorkflowConfig,
  AutoLoanWorkflowConfigUpsertPayload,
  AutoLoanWorkflowConfigsListResponse
} from "../../types"
import {
  AUTO_LOAN_WORKFLOW_CONFIGS_ENDPOINT,
  AUTO_LOAN_WORKFLOW_CONFIG_ENDPOINT,
  RTK_SLICE_API_LIST
} from "../../common/constants"
import bbApi from "../../api"
import { baseQuery, getTags } from "../api"
import { getQueryString } from "../../common/utils"

const reducerPath = "auto-loan-workflow"

export const autoLoanWorkflowApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath],
  endpoints: builder => ({
    findAutoLoanWorkflows: builder.query({
      query: (brand_id: string) =>
        `${AUTO_LOAN_WORKFLOW_CONFIGS_ENDPOINT}?brand_id=${brand_id}`,
      transformResponse: (responseData: AutoLoanWorkflowConfigsListResponse) =>
        responseData?.auto_loan_workflow_configs ?? [],
      providesTags: result => getTags(reducerPath, "id", result)
    }),
    fetchAutoLoanWorkflowDetails: builder.query({
      query: (params: { id: string }) =>
        `${AUTO_LOAN_WORKFLOW_CONFIG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: AutoLoanWorkflowConfig) => responseData,
      providesTags: (result, error, arg) => [{ type: reducerPath, id: arg.id }]
    }),
    /**
     * No id resolves to the built-in wizard (`id: 0`) — the starting point a
     * new config is seeded from.
     */
    fetchDefaultAutoLoanWorkflowDetails: builder.query({
      query: () => `${AUTO_LOAN_WORKFLOW_CONFIG_ENDPOINT}?id=`,
      transformResponse: (responseData: AutoLoanWorkflowConfig) => responseData
    }),
    updateOrCreateAutoLoanWorkflow: builder.mutation({
      queryFn: async (payload: AutoLoanWorkflowConfigUpsertPayload) => {
        const { id, ...rest } = payload
        // An empty/zero id means CREATE — id 0 is the built-in fallback, never
        // a row that can be updated.
        const body: AutoLoanWorkflowConfigUpsertPayload = Number(id)
          ? { ...rest, id: Number(id) }
          : rest
        const data = await bbApi.autoLoanWorkflow.updateOrCreate(body)
        return { data }
      },
      invalidatesTags: (result, error, arg) => {
        const resultData = result as
          | { status?: string; message?: string }
          | undefined
        // Validation rejections come back HTTP 200 with status "error" — don't
        // invalidate, the stored config hasn't changed.
        if (error || resultData?.status === "error") {
          return []
        }
        return [
          { type: reducerPath, id: arg.id },
          { type: reducerPath, id: RTK_SLICE_API_LIST }
        ]
      }
    }),
    deleteAutoLoanWorkflow: builder.mutation({
      queryFn: async (id: string | number) => {
        const data = await bbApi.autoLoanWorkflow.delete(id)
        return { data }
      },
      invalidatesTags: () => [{ type: reducerPath, id: RTK_SLICE_API_LIST }]
    })
  })
})

export const {
  useFindAutoLoanWorkflowsQuery,
  useFetchAutoLoanWorkflowDetailsQuery,
  useFetchDefaultAutoLoanWorkflowDetailsQuery,
  useUpdateOrCreateAutoLoanWorkflowMutation,
  useDeleteAutoLoanWorkflowMutation
} = autoLoanWorkflowApi
